import { EventTemplate, NostrEvent } from "nostr-tools";

import {
  GAME_KIND,
  GameMeta,
  GameStatus,
  MOVE_KIND,
  SIDE_BET_KIND,
  SIDE_BET_TAKE_KIND,
  SideBetOffer,
  SideBetStatus,
  SideBetTake,
} from "./types";

/** Create a kind 30069 game event (parameterized replaceable) */
export function createGameEvent(meta: GameMeta): EventTemplate {
  const tags: string[][] = [
    ["d", meta.gameId],
    ["game", meta.gameType],
    ["bet", String(meta.betAmount)],
    ["status", meta.status],
    ["escrow", meta.escrowPubkey],
    ["mint", meta.mintUrl],
  ];

  if (meta.moveTimeoutSeconds) tags.push(["timeout", String(meta.moveTimeoutSeconds)]);
  if (meta.opponent) tags.push(["p", meta.opponent]);
  if (meta.aiDifficulty) tags.push(["ai", meta.aiDifficulty]);

  const content: Record<string, unknown> = {
    escrowPubkey: meta.escrowPubkey,
    creatorProofs: meta.creatorProofs,
  };
  if (meta.opponentProofs) content.opponentProofs = meta.opponentProofs;
  if (meta.encryptedEscrowPrivkey) content.encryptedEscrowPrivkey = meta.encryptedEscrowPrivkey;
  if (meta.winner) content.winner = meta.winner;

  return {
    kind: GAME_KIND,
    created_at: Math.floor(Date.now() / 1000),
    content: JSON.stringify(content),
    tags,
  };
}

/** Create a kind 1069 move event */
export function createMoveEvent(
  gameAddress: string,
  opponent: string,
  seq: number,
  moveContent: Record<string, unknown>,
): EventTemplate {
  return {
    kind: MOVE_KIND,
    created_at: Math.floor(Date.now() / 1000),
    content: JSON.stringify(moveContent),
    tags: [
      ["a", gameAddress],
      ["p", opponent],
      ["seq", String(seq)],
    ],
  };
}

/** Parse a kind 30069 game event into GameMeta */
export function parseGameEvent(event: NostrEvent): GameMeta | null {
  try {
    const getTag = (name: string) => event.tags.find((t) => t[0] === name)?.[1];

    const gameId = getTag("d");
    const gameType = getTag("game") || "qawale";
    const status = getTag("status") as GameStatus | undefined;
    const escrowPubkey = getTag("escrow");
    const mintUrl = getTag("mint");
    const betAmount = Number(getTag("bet"));
    const opponent = getTag("p");
    const timeoutStr = getTag("timeout");
    const moveTimeoutSeconds = timeoutStr ? Number(timeoutStr) : undefined;
    const aiDifficulty = getTag("ai") || undefined;

    if (!gameId || !status || !escrowPubkey || !mintUrl || isNaN(betAmount)) return null;

    const content = JSON.parse(event.content);

    return {
      gameId,
      gameType,
      creator: event.pubkey,
      opponent,
      betAmount,
      mintUrl,
      status,
      escrowPubkey,
      creatorProofs: content.creatorProofs,
      opponentProofs: content.opponentProofs,
      encryptedEscrowPrivkey: content.encryptedEscrowPrivkey,
      winner: content.winner,
      moveTimeoutSeconds,
      aiDifficulty,
    };
  } catch {
    return null;
  }
}

/** Parse a kind 1069 move event — returns raw move data, engine validates */
export function parseMoveEvent(event: NostrEvent): { seq: number; moveData: unknown } | null {
  try {
    const seqTag = event.tags.find((t) => t[0] === "seq")?.[1];
    if (!seqTag) return null;

    const seq = Number(seqTag);
    if (isNaN(seq) || seq < 0) return null;

    const moveData = JSON.parse(event.content);
    return { seq, moveData };
  } catch {
    return null;
  }
}

/** Build the "a" tag value for a game: "30069:<creator>:<gameId>" */
export function getGameAddress(creator: string, gameId: string): string {
  return `${GAME_KIND}:${creator}:${gameId}`;
}

/** Create a kind 31070 side-bet offer event (parameterized replaceable) */
export function createSideBetOfferEvent(offer: {
  betId: string;
  gameAddress: string;
  side: "player1" | "player2";
  amount: number;
  mintUrl: string;
  escrowPubkey: string;
  offerorProofs: string;
  status: SideBetStatus;
  takerProofs?: string;
  encryptedEscrowPrivkey?: string;
}): EventTemplate {
  const tags: string[][] = [
    ["d", offer.betId],
    ["a", offer.gameAddress],
    ["side", offer.side],
    ["amount", String(offer.amount)],
    ["mint", offer.mintUrl],
    ["status", offer.status],
    ["escrow", offer.escrowPubkey],
  ];

  const content: Record<string, unknown> = {
    escrowPubkey: offer.escrowPubkey,
    offerorProofs: offer.offerorProofs,
  };
  if (offer.takerProofs) content.takerProofs = offer.takerProofs;
  if (offer.encryptedEscrowPrivkey) content.encryptedEscrowPrivkey = offer.encryptedEscrowPrivkey;

  return {
    kind: SIDE_BET_KIND,
    created_at: Math.floor(Date.now() / 1000),
    content: JSON.stringify(content),
    tags,
  };
}

/** Create a kind 1071 side-bet take event */
export function createSideBetTakeEvent(
  gameAddress: string,
  offerEventId: string,
  takerProofs: string,
): EventTemplate {
  return {
    kind: SIDE_BET_TAKE_KIND,
    created_at: Math.floor(Date.now() / 1000),
    content: JSON.stringify({ takerProofs }),
    tags: [
      ["a", gameAddress],
      ["e", offerEventId],
    ],
  };
}

/** Parse a kind 31070 side-bet offer event */
export function parseSideBetOffer(event: NostrEvent): SideBetOffer | null {
  try {
    const getTag = (name: string) => event.tags.find((t) => t[0] === name)?.[1];

    const betId = getTag("d");
    const gameAddress = getTag("a");
    const side = getTag("side") as "player1" | "player2" | undefined;
    const amount = Number(getTag("amount"));
    const mintUrl = getTag("mint");
    const status = getTag("status") as SideBetStatus | undefined;
    const escrowPubkey = getTag("escrow");

    if (!betId || !gameAddress || !side || !mintUrl || !status || !escrowPubkey || isNaN(amount)) {
      return null;
    }

    const content = JSON.parse(event.content);

    return {
      betId,
      offerer: event.pubkey,
      gameAddress,
      side,
      amount,
      mintUrl,
      escrowPubkey,
      offerorProofs: content.offerorProofs,
      status,
      takerProofs: content.takerProofs,
      encryptedEscrowPrivkey: content.encryptedEscrowPrivkey,
    };
  } catch {
    return null;
  }
}

/** Parse a kind 1071 side-bet take event */
export function parseSideBetTake(event: NostrEvent): SideBetTake | null {
  try {
    const offerEventId = event.tags.find((t) => t[0] === "e")?.[1];
    if (!offerEventId) return null;

    const content = JSON.parse(event.content);
    if (!content.takerProofs) return null;

    return {
      eventId: event.id,
      offerId: offerEventId,
      taker: event.pubkey,
      takerProofs: content.takerProofs,
    };
  } catch {
    return null;
  }
}
